/**
 * VenuesBulkRequestDialog — send the venue-onboarding request form to
 * N selected venues in one operator action.
 *
 * Per-venue we POST /api/venues/request/create-link with a prefill
 * derived from the venue row (chain, address, format) and the venue's
 * tech contact as the recipient. Venues without a tech_contact_email
 * are flagged for the operator and skipped from the auto-send pass
 * (they'll need a copy-link path instead).
 *
 * Sends are throttled to 4 in-flight requests at a time so we don't
 * stampede the SMTP path when the operator selects hundreds of rows.
 */
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, TextField, Alert, Switch, FormControlLabel,
  LinearProgress, List, ListItem, ListItemText, Chip, Divider,
} from '@mui/material';
import api from '../services/api';


const CONCURRENCY = 4;


async function _runWithConcurrency(items, worker, onProgress) {
  const results = new Array(items.length);
  let next = 0;
  let done = 0;
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, async () => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      try {
        results[i] = await worker(items[i], i);
      } catch (e) {
        results[i] = { error: e?.response?.data?.detail || e?.message || 'Failed' };
      }
      done++;
      onProgress?.(done, items.length);
    }
  }));
  return results;
}


function _prefillFor(venue) {
  return {
    chain: venue.chain || '',
    venue_name: venue.name || '',
    screen_names: (venue.screens || []).map(s => s.name).filter(Boolean),
    city: venue.city || '',
    state: venue.state || '',
    country: venue.country || 'US',
    zip_code: venue.zip_code || '',
    street_address: venue.address || '',
    timezone: venue.timezone || '',
    tech_contact_phone: venue.tech_contact_phone || '',
    // Don't pre-pin format/resolution — let the venue answer fresh so we
    // catch projector changes since the last KDM.
  };
}


export default function VenuesBulkRequestDialog({ open, venues, onClose, onComplete }) {
  const [expiresDays, setExpiresDays] = React.useState(14);
  const [sendEmail, setSendEmail] = React.useState(true);
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [progress, setProgress] = React.useState({ done: 0, total: 0 });
  const [results, setResults] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setSending(false);
      setProgress({ done: 0, total: 0 });
      setResults(null);
      setMessage('');
      setExpiresDays(14);
      setSendEmail(true);
    }
  }, [open]);

  const withEmail = (venues || []).filter(v => v.tech_contact_email);
  const withoutEmail = (venues || []).filter(v => !v.tech_contact_email);

  const handleSend = async () => {
    setSending(true);
    setProgress({ done: 0, total: withEmail.length });
    const res = await _runWithConcurrency(withEmail, async (venue) => {
      const body = {
        title: `${venue.name}${venue.chain ? ' (' + venue.chain + ')' : ''}`,
        recipient_name: venue.tech_contact_name || venue.name,
        recipient_email: venue.tech_contact_email,
        expires_days: expiresDays,
        send_email: sendEmail,
        message: message || '',
        prefill: _prefillFor(venue),
      };
      const r = await api.post('/api/venues/request/create-link', body);
      return { venue, ...r.data };
    }, (done, total) => setProgress({ done, total }));
    setResults(res);
    setSending(false);
    onComplete?.(res);
  };

  const successCount = (results || []).filter(r => !r.error).length;
  const errorCount = (results || []).filter(r => r.error).length;

  return (
    <Dialog open={open} onClose={() => !sending && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle>
        Send request form to {venues?.length || 0} venue{venues?.length === 1 ? '' : 's'}
      </DialogTitle>
      <DialogContent>
        {!results ? (
          <>
            {withoutEmail.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {withoutEmail.length} venue{withoutEmail.length === 1 ? '' : 's'} {withoutEmail.length === 1 ? 'has' : 'have'} no tech contact email and will be skipped. Add a tech contact first, or send those individually with the copy-link flow.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each venue gets its own form-token URL. Their tech contact is the recipient. Prefilled fields (chain, address, screens, timezone) come from the venue record so the venue only confirms what's changed.
            </Typography>
            <TextField
              label="Expires (days)"
              type="number"
              value={expiresDays}
              onChange={(e) => setExpiresDays(Math.max(1, Math.min(60, Number(e.target.value) || 14)))}
              size="small"
              sx={{ width: 140, mr: 2 }}
              inputProps={{ min: 1, max: 60 }}
            />
            <FormControlLabel
              control={<Switch checked={sendEmail} onChange={(_e, v) => setSendEmail(v)} />}
              label="Email the venues now"
            />
            <TextField
              label="Message (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Short note included in the email body, above the form link."
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              sx={{ mt: 2 }}
            />
            {sending && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Sending {progress.done} / {progress.total}…
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress.total ? (progress.done / progress.total) * 100 : 0}
                />
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip color="success" label={`${successCount} sent`} />
              {errorCount > 0 && <Chip color="error" label={`${errorCount} failed`} />}
              {withoutEmail.length > 0 && <Chip color="warning" label={`${withoutEmail.length} skipped (no email)`} />}
            </Box>
            <Divider sx={{ mb: 1 }} />
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {results.map((r, i) => (
                <ListItem key={i}>
                  <ListItemText
                    primary={r.venue?.name}
                    secondary={
                      r.error
                        ? `Failed: ${r.error}`
                        : `${r.email_sent ? 'Emailed ' + (r.recipient_name || r.venue?.tech_contact_email) : 'Link created'} — expires ${r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '?'}`
                    }
                  />
                </ListItem>
              ))}
              {withoutEmail.map((v) => (
                <ListItem key={`skip-${v.id}`}>
                  <ListItemText
                    primary={v.name}
                    secondary="Skipped — no tech contact email"
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={sending}>{results ? 'Close' : 'Cancel'}</Button>
        {!results && (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || withEmail.length === 0}
          >
            {sending ? 'Sending…' : `Send to ${withEmail.length}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
